import React, { useState, lazy, Suspense } from 'react';
import './CodeBlock.css';

// Use lazy loading for the syntax highlighter to improve performance
const SyntaxHighlighter = lazy(() => import('react-syntax-highlighter/dist/esm/prism-light'));
const { oneDark } = lazy(() => import('react-syntax-highlighter/dist/esm/styles/prism'));

// Language registration map - only import languages when needed
const languageMap = {
  javascript: () => import('react-syntax-highlighter/dist/esm/languages/prism/javascript'),
  js: () => import('react-syntax-highlighter/dist/esm/languages/prism/javascript'),
  jsx: () => import('react-syntax-highlighter/dist/esm/languages/prism/jsx'),
  typescript: () => import('react-syntax-highlighter/dist/esm/languages/prism/typescript'),
  ts: () => import('react-syntax-highlighter/dist/esm/languages/prism/typescript'),
  python: () => import('react-syntax-highlighter/dist/esm/languages/prism/python'),
  py: () => import('react-syntax-highlighter/dist/esm/languages/prism/python'),
  bash: () => import('react-syntax-highlighter/dist/esm/languages/prism/bash'),
  sh: () => import('react-syntax-highlighter/dist/esm/languages/prism/bash'),
  json: () => import('react-syntax-highlighter/dist/esm/languages/prism/json'),
  css: () => import('react-syntax-highlighter/dist/esm/languages/prism/css'),
  html: () => import('react-syntax-highlighter/dist/esm/languages/prism/markup'),
  xml: () => import('react-syntax-highlighter/dist/esm/languages/prism/markup'),
  sql: () => import('react-syntax-highlighter/dist/esm/languages/prism/sql'),
};

/**
 * CodeBlock component with syntax highlighting and copy functionality
 * Lazy-loads languages as needed to improve performance
 */
const CodeBlock = ({ className, children }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isLanguageLoaded, setIsLanguageLoaded] = useState(false);
  
  // Extract language from className if provided
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  const codeText = String(children).replace(/\n$/, '');
  
  // Handle copy to clipboard functionality
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(codeText);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = codeText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  // Load language if it's in our map
  React.useEffect(() => {
    if (language && languageMap[language]) {
      // Dynamically import the language
      const loadLanguage = async () => {
        try {
          // Import and register the language
          const langModule = await languageMap[language]();
          const SyntaxHighlighterModule = await import('react-syntax-highlighter/dist/esm/prism-light');
          SyntaxHighlighterModule.default.registerLanguage(language, langModule.default);
          setIsLanguageLoaded(true);
        } catch (error) {
          console.error(`Error loading language: ${language}`, error);
          // Continue without highlighting if language fails to load
          setIsLanguageLoaded(true);
        }
      };
      
      loadLanguage();
    } else {
      // If no language specified or not in our map, don't wait for loading
      setIsLanguageLoaded(true);
    }
  }, [language]);

  return (
    <div className="code-block-container" dir="ltr" lang="en">
      <button 
        className={`copy-button ${isCopied ? 'copied' : ''}`}
        onClick={copyToClipboard}
        aria-label="Copy code"
      >
        {isCopied ? 'COPIED ✓' : 'COPY'}
      </button>
      
      <Suspense fallback={<pre className="code-loading" dir="ltr"><code>{codeText}</code></pre>}>
        {isLanguageLoaded ? (
          <SyntaxHighlighter
            language={language}
            style={oneDark}
            customStyle={{
              margin: 0,
              padding: '1rem',
              background: '#282c34',
              fontSize: '14px',
              lineHeight: '1.5',
              borderRadius: '8px',
              direction: 'ltr',
              textAlign: 'left'
            }}
            showLineNumbers={true}
            wrapLines={true}
          >
            {codeText}
          </SyntaxHighlighter>
        ) : (
          <pre className="code-loading" dir="ltr"><code>{codeText}</code></pre>
        )}
      </Suspense>
    </div>
  );
};

export default CodeBlock;
